import { defineComponent as _defineComponent } from 'vue';
import BarsChart from "@/components/common/bars-chart/BarsChart.vue";
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { calculateChartHeight, createResponsiveChartConfig } from "../../lib/chart-config-builder";
export default /*@__PURE__*/ _defineComponent({
    __name: 'BaseKpiChart',
    props: {
        chartConfig: { type: Object, required: true },
        transformData: { type: Function, required: true },
        chartType: { type: null, required: true },
        chartData: { type: null, required: true },
        workingDaysProgress: { type: [Object, null], required: true },
        loading: { type: Boolean, required: true },
        error: { type: [String, null], required: true },
        maxDecimalPlaces: { type: Number, required: false, default: 3 },
        minDecimalPlaces: { type: Number, required: false, default: 0 },
        showThreshold: { type: Boolean, required: false, default: true },
        showDataLabels: { type: Boolean, required: false, default: true }
    },
    setup(__props, { expose: __expose }) {
        __expose();
        const props = __props;
        // Template refs
        const chartContainer = ref();
        // Reactive height management
        const windowHeight = ref(window.innerHeight);
        const chartHeight = computed(() => calculateChartHeight(windowHeight.value));
        // Reactive chart data
        const chartData = reactive({
            labels: [],
            datasets: [],
        });
        // Check if data is empty
        const isEmptyData = computed(() => {
            if (!props.chartData)
                return true;
            if (Array.isArray(props.chartData)) {
                return props.chartData.length === 0;
            }
            return false;
        });
        // Computed responsive chart options with dynamic y-axis
        const responsiveChartOptions = computed(() => {
            var _a;
            const isSmallScreen = chartHeight.value < 400;
            const configWithData = Object.assign(Object.assign({}, props.chartConfig), { scales: Object.assign(Object.assign({}, props.chartConfig.scales), { y: Object.assign(Object.assign({}, (_a = props.chartConfig.scales) === null || _a === void 0 ? void 0 : _a.y), { max: getMaxValueFromChartData() }) }) });
            return createResponsiveChartConfig(configWithData, isSmallScreen);
        });
        // Get maximum value from chart data
        function getMaxValueFromChartData() {
            if (!chartData.datasets || chartData.datasets.length === 0) {
                return 0;
            }
            let maxValue = 0;
            chartData.datasets.forEach((dataset) => {
                if (dataset.data && Array.isArray(dataset.data)) {
                    dataset.data.forEach((value) => {
                        if (typeof value === "number" && value > maxValue) {
                            maxValue = value;
                        }
                    });
                }
            });
            const roundedMax = Math.ceil(maxValue / 50) * 50;
            return Math.max(100, roundedMax);
        }
        const showThreshold = computed(() => props.showThreshold && props.workingDaysProgress !== null);
        // Update chart data when props change
        function updateChartData() {
            if (!props.chartData) {
                chartData.labels = [];
                chartData.datasets = [];
                return;
            }
            const newData = props.transformData(props.chartData, props.workingDaysProgress);
            chartData.labels = newData.labels;
            chartData.datasets = newData.datasets;
        }
        // Window resize handler
        function handleResize() {
            windowHeight.value = window.innerHeight;
        }
        // Lifecycle hooks
        onMounted(() => {
            window.addEventListener("resize", handleResize);
            updateChartData();
        });
        onUnmounted(() => {
            window.removeEventListener("resize", handleResize);
        });
        // Watch for data changes
        watch(() => [props.chartType, props.chartData, props.workingDaysProgress], () => {
            updateChartData();
        }, { deep: true });
        const __returned__ = { props, chartContainer, windowHeight, chartHeight, chartData, isEmptyData, responsiveChartOptions, getMaxValueFromChartData, showThreshold, updateChartData, handleResize, BarsChart };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
